import type { CapitalGlobalProposalStepProps } from '@/features/proposals/components/capital-global/capital-global-proposal-step.types';
import { CapitalGlobalProposalConfirmSignatureMethodModal } from '@/features/proposals/components/capital-global/steps/sections/capital-global-proposal-confirm-signature-method-modal.component';
import { CapitalGlobalProposalDocusignStatusSection } from '@/features/proposals/components/capital-global/steps/sections/capital-global-proposal-docusign-status-section.component';
import { CapitalGlobalProposalResumeFooter } from '@/features/proposals/components/capital-global/steps/sections/capital-global-proposal-resume-footer.component';
import { CapitalGlobalProposalResumePremiumSection } from '@/features/proposals/components/capital-global/steps/sections/capital-global-proposal-resume-premium-section.component';
import { useCapitalGlobalProposalStep3ViewModel } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-step3-view-model.hook';
import {
  CalculationCommercializationSection,
  CalculationStipulantsListSection,
  CalculationSummaryCardSection,
} from '@/features/quotes/components/capital-global/steps/sections/step-calculation.sections';
import { Spinner } from '@noorden-insurtech/alba-design-system';

export function StepCapitalGlobalResumo(props: CapitalGlobalProposalStepProps) {
  const {
    premiumData,
    calculationData,
    isLoading,
    signature,
    handleGeneratePdf,
    openPhysicalSignatureConfirm,
    openDigitalSignatureConfirm,
    closeSignatureMethodConfirm,
    confirmSignatureMethod,
    isSignatureMethodConfirmOpen,
    isSignatureMethodConfirmLoading,
    handleSyncSignature,
    handleRetrySignature,
    isGeneratePdfPending,
    canGeneratePdfInViewMode,
    isTransmitted,
    shouldHideSignatureActions,
    isPhysicalSignaturePending,
    isCreateSignaturePending,
    isSyncSignaturePending,
    isRetrySignaturePending,
    isPhysicalSignatureBlocked,
    physicalSignatureBlockedReason,
    digitalSignatureBlockedReason,
    canStartDigital,
    canSync,
    canRetry,
    isSignatureActionsBlocked,
    signatureActionsBlockedReason,
    isGeneratePdfBlocked,
    generatePdfBlockedReason,
    canGoToUploadStep,
    onGoToUploadStep,
  } = useCapitalGlobalProposalStep3ViewModel({
    proposal: props.proposal,
    policyId: props.policyId,
    isViewMode: props.isViewMode,
    onNext: props.onNext,
    canGoNext: props.canGoNext,
  });

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Spinner size='lg' colorScheme='blue' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-10'>
      <CapitalGlobalProposalResumePremiumSection data={premiumData} />

      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <CalculationSummaryCardSection
          data={calculationData.insuranceDataCard}
        />
        <CalculationSummaryCardSection
          data={calculationData.stipulantDataCard}
        />
      </div>

      <CalculationStipulantsListSection
        entries={calculationData.stipulantAccordions}
      />

      <CalculationCommercializationSection
        data={calculationData.commercialization}
      />

      {signature && (
        <CapitalGlobalProposalDocusignStatusSection
          signature={signature}
          isViewMode={props.isViewMode}
          canSync={canSync}
          canRetry={canRetry}
          isSyncPending={isSyncSignaturePending}
          isRetryPending={isRetrySignaturePending}
          onSync={() => {
            void handleSyncSignature();
          }}
          onRetry={() => {
            void handleRetrySignature();
          }}
          actionsDisabled={isSignatureActionsBlocked}
          actionsDisabledReason={signatureActionsBlockedReason}
        />
      )}

      <CapitalGlobalProposalResumeFooter
        isViewMode={props.isViewMode}
        canGoBack={props.canGoBack}
        onBack={props.onBack}
        canGoToUploadStep={canGoToUploadStep}
        onGoToUploadStep={onGoToUploadStep}
        onGeneratePdf={() => {
          void handleGeneratePdf();
        }}
        onPhysicalSignature={openPhysicalSignatureConfirm}
        onDigitalSignature={openDigitalSignatureConfirm}
        isGeneratePdfPending={isGeneratePdfPending}
        canGeneratePdfInViewMode={canGeneratePdfInViewMode}
        isTransmitted={isTransmitted}
        shouldHideSignatureActions={shouldHideSignatureActions}
        isPhysicalSignaturePending={isPhysicalSignaturePending}
        isCreateSignaturePending={isCreateSignaturePending}
        isPhysicalSignatureBlocked={isPhysicalSignatureBlocked}
        physicalSignatureBlockedReason={physicalSignatureBlockedReason}
        isDigitalSignatureDisabled={!canStartDigital}
        digitalSignatureBlockedReason={digitalSignatureBlockedReason}
        isGeneratePdfBlocked={isGeneratePdfBlocked}
        generatePdfBlockedReason={generatePdfBlockedReason}
      />

      <CapitalGlobalProposalConfirmSignatureMethodModal
        isOpen={isSignatureMethodConfirmOpen}
        isConfirmLoading={isSignatureMethodConfirmLoading}
        onConfirm={() => {
          void confirmSignatureMethod();
        }}
        onClose={closeSignatureMethodConfirm}
      />
    </div>
  );
}
